import React from 'react';
import { Check, Sparkles } from 'lucide-react';

/**
 * Heavily-emphasised licence-fee callout banner used across every profile page.
 * Shows a live "X months left" countdown pulled from the June 30 2027 deadline.
 *
 * @param {string} text  - The pill text (defaults to school-facing copy)
 * @param {string} align - 'left' (default, inline) or 'center' (full-width band)
 * @param {Date}   deadline - Deadline used for the countdown (default: 2027-06-30)
 */
function computeRemaining(deadline) {
  const now = new Date();
  if (now >= deadline) return 'ENDED';
  const yearsDiff = deadline.getFullYear() - now.getFullYear();
  const monthsDiff = deadline.getMonth() - now.getMonth();
  let months = yearsDiff * 12 + monthsDiff;
  if (deadline.getDate() < now.getDate()) months -= 1;
  if (months >= 12) {
    const y = Math.floor(months / 12);
    const m = months % 12;
    if (m === 0) return `${y} year${y > 1 ? 's' : ''} left`;
    return `${y}y ${m}m left`;
  }
  if (months >= 1) return `${months} month${months > 1 ? 's' : ''} left`;
  const days = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  return `${days} day${days > 1 ? 's' : ''} left`;
}

export default function LicenceFeeHighlight({
  text = 'Complete software ecosystem with no licence fee through June 2027.',
  align = 'left',
  deadline = new Date('2027-06-30T23:59:59Z'),
}) {
  const remaining = computeRemaining(deadline);

  const Card = (
    <div className="relative inline-flex items-center gap-2.5 rounded-2xl bg-gradient-to-r from-mint-50 via-cyan-50 to-purple-50 px-5 py-3.5 shadow-soft ring-2 ring-mint/30" data-testid="licence-fee-highlight">
      <span className="grid h-8 w-8 place-items-center rounded-full bg-mint text-white shadow"><Check className="h-4 w-4" /></span>
      <span className="text-[14px] font-bold text-navy">{text}</span>
      <span
        className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-brand-gradient px-2.5 py-0.5 text-[10px] font-extrabold tracking-wide text-white shadow"
        data-testid="licence-fee-limited-badge"
        aria-label={`Limited access ends June 2027 — ${remaining}`}
        title={`Access ends 30 Jun 2027 — ${remaining}`}
      >
        <Sparkles className="h-3 w-3" />
        <span>LIMITED</span>
        {remaining !== 'ENDED' && (
          <>
            <span className="opacity-60">·</span>
            <span data-testid="licence-fee-countdown">{remaining.toUpperCase()}</span>
          </>
        )}
      </span>
    </div>
  );

  if (align === 'center') {
    return <div className="w-full flex justify-center px-6">{Card}</div>;
  }
  return Card;
}
